import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Target,
  MessagesSquare,
  BarChart3,
  Sparkles,
  Layers,
  Settings,
  Crown,
} from 'lucide-react';

/**
 * The .pen "— Web" nav spine. A single left sidebar (desktop) / bottom tab bar
 * (mobile) replaces the old top Header on the logged-in app surface.
 *
 * This module holds the PURE, testable pieces: the nav item config, the
 * route → "is this item active" mapping, and the "should the app shell show on
 * this route" predicate. Keeping them pure means we unit-test the gating without
 * rendering React (see appShell.test.ts).
 */

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Label passed to trackNavigationClick — preserves the Header analytics. */
  analyticsLabel: string;
}

/** Primary sidebar nav, in the .pen order. */
export const PRIMARY_NAV_ITEMS: readonly NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, analyticsLabel: 'Dashboard' },
  { label: 'Practice', path: '/practice', icon: Target, analyticsLabel: 'Practice' },
  { label: 'Clinical Sims', path: '/sims', icon: MessagesSquare, analyticsLabel: 'Clinical Sims' },
  { label: 'Progress', path: '/progress', icon: BarChart3, analyticsLabel: 'Progress' },
  { label: 'Ask Pam', path: '/ask-pam', icon: Sparkles, analyticsLabel: 'Ask Pam' },
  { label: 'Flashcards', path: '/flashcards', icon: Layers, analyticsLabel: 'Flashcards' },
];

/**
 * The upgrade door for signed-in users who have not bought yet.
 *
 * Why it exists: before this, a logged-in user had NO route to the offer at
 * all. The app shell replaces the marketing Header + Footer on /dashboard,
 * /practice, /progress etc., and neither the sidebar nor the bottom tab bar
 * carried a pricing entry, so the only $129 surfaces (homepage copy, the
 * diagnostic reveal) were unreachable once you signed in. GA4 (90d) recorded
 * 5 pricing_page_viewed and 0 subscription_clicked as a result.
 *
 * Rendered ONLY when the user lacks access, gated on the authoritative backend
 * flag (`hasFullAccess`) so people who already paid are never nagged.
 */
export const UPGRADE_NAV_ITEM: NavItem = {
  label: 'Upgrade',
  path: '/pricing',
  icon: Crown,
  analyticsLabel: 'Upgrade',
};

/**
 * True when the upgrade nav entry should render. `hasFullAccess` is the
 * backend's authoritative entitlement flag; while it is still loading
 * (`undefined`) we withhold the nudge rather than flash it at a paying user.
 */
export function shouldShowUpgradeNav(hasFullAccess: boolean | undefined): boolean {
  return hasFullAccess === false;
}

/** Rule 1: hide Practice while the trial dashboard owns /dashboard. */
export function filterNavForTrialWeek<T extends { path: string }>(
  items: readonly T[],
  hidePractice: boolean,
): T[] {
  if (!hidePractice) return [...items];
  return items.filter((item) => item.path !== '/practice');
}

/** Settings is pinned to the bottom of the sidebar in the .pen. */
export const SETTINGS_NAV_ITEM: NavItem = {
  label: 'Settings',
  path: '/settings',
  icon: Settings,
  analyticsLabel: 'Settings',
};

/**
 * Mobile bottom tab bar — a 5-item subset (the .pen iPhone frames + iOS parity).
 * Clinical Sims is dropped on mobile to keep 5 comfortable tap targets; it stays
 * reachable from the Dashboard.
 */
export const MOBILE_NAV_ITEMS: readonly NavItem[] = [
  PRIMARY_NAV_ITEMS[0], // Dashboard
  PRIMARY_NAV_ITEMS[1], // Practice
  PRIMARY_NAV_ITEMS[3], // Progress
  PRIMARY_NAV_ITEMS[4], // Ask Pam
  PRIMARY_NAV_ITEMS[5], // Flashcards
];

/**
 * Route prefixes that make up the logged-in "app" surface — the only place the
 * sidebar/bottom-bar shell renders. Everything else (landing, about, blog,
 * pricing/paywall, sign-in, sign-up, onboarding, legal, and the full-screen quiz
 * player) keeps the marketing Header + Footer instead.
 */
const APP_SHELL_PREFIXES: readonly string[] = [
  '/dashboard',
  '/practice',
  '/sims',
  '/progress',
  '/ask-pam',
  '/flashcards',
  '/settings',
  '/exam-results',
  '/feedback',
];

/**
 * True when the given route should render the left-sidebar / bottom-bar shell.
 * Routes that own their chrome (quiz player, sim session) are excluded so they
 * stay immersive.
 */
export function shouldShowAppShell(pathname: string): boolean {
  if (ownsItsChrome(pathname)) return false;
  return APP_SHELL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}

/**
 * True when a nav item should render as the selected pill for the current route.
 * Exact match, or a nested child route (e.g. `/sims/:id` keeps "Clinical Sims"
 * active). A sibling that merely shares a string prefix (`/practices`) does NOT
 * match.
 */
export function isActiveNavPath(itemPath: string, pathname: string): boolean {
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

/**
 * True when a route owns its ENTIRE chrome and must render with neither the app
 * shell nor the marketing Header/Footer: the full-screen quiz player, a sim
 * session, the .pen two-panel sign-in, and the distraction-free retake landing.
 *
 * The public licensing pages are deliberately NOT here. They shipped in #418
 * with a nav and footer of their own, which left the site with two different
 * chromes and no route from the marketing pages into /licensing. One header,
 * one footer, every surface.
 */
export function ownsItsChrome(pathname: string): boolean {
  return (
    pathname.startsWith('/quiz/') ||
    pathname.startsWith('/sims/') ||
    pathname === '/signin' ||
    pathname === '/retake'
  );
}

const RENDERS_BEFORE_AUTH_EXACT = new Set(['/', '/try', '/lbsw', '/about', '/privacy-policy', '/privacy', '/terms', '/disclosures']);
const RENDERS_BEFORE_AUTH_PREFIXES = ['/blog', '/licensing', '/programs'];

/**
 * Public routes that render before Firebase Auth reports its first state. Each one reads
 * auth as signed out until it resolves (Landing's dashboard redirect waits for it itself).
 * /pricing is excluded: its checkout handler branches on currentUser.
 */
export function rendersBeforeAuth(pathname: string): boolean {
  return (
    RENDERS_BEFORE_AUTH_EXACT.has(pathname) ||
    RENDERS_BEFORE_AUTH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'))
  );
}
